import { useState, useMemo } from "react";
import { Link, Navigate, useSearchParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Activity, ShieldCheck, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

export default function ResetPassword() {
  const { user, loading } = useAuth();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = useMemo(() => params.get("token") || "", [params]);
  const [pwd, setPwd] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (loading) return null;
  if (user) return <Navigate to="/change-password" replace />;

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50 p-6">
        <Card className="w-full max-w-md p-8 border border-zinc-200 shadow-sm" data-testid="reset-password-missing-token">
          <h1 className="font-display text-xl font-bold text-zinc-950">Invalid reset link</h1>
          <p className="mt-2 text-sm text-zinc-600">
            This link is missing its token. Please request a new password-reset email from the sign-in page.
          </p>
          <Link to="/forgot-password" className="mt-4 inline-flex items-center gap-1.5 text-sm text-zinc-700 hover:text-zinc-950 underline">
            Request a new link
          </Link>
        </Card>
      </div>
    );
  }

  async function onSubmit(e) {
    e.preventDefault();
    if (pwd.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    if (pwd !== confirm) {
      toast.error("Passwords do not match");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pwd });
      toast.success("Password updated — you can now sign in");
      navigate("/login", { replace: true });
    } catch (e) {
      toast.error("Could not reset password", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-zinc-50 p-6" data-testid="reset-password-page">
      <Card className="w-full max-w-md p-8 border border-zinc-200 shadow-sm">
        <div className="flex items-center gap-2.5 mb-6">
          <div className="h-9 w-9 grid place-items-center bg-zinc-950 text-white rounded-md">
            <Activity className="h-4 w-4" strokeWidth={2.25} />
          </div>
          <div className="font-display text-xl font-bold tracking-tight text-zinc-950">
            Acuity<span className="text-zinc-400">/</span>Reports
          </div>
        </div>

        <h1 className="font-display text-2xl font-bold tracking-tight text-zinc-950 flex items-center gap-2">
          <ShieldCheck className="h-5 w-5" /> Choose a new password
        </h1>
        <p className="mt-1 text-sm text-zinc-600">
          Pick something memorable but secure. You'll use this from now on to sign in.
        </p>

        <form onSubmit={onSubmit} className="mt-6 space-y-4">
          <div>
            <Label htmlFor="rp-new">New password</Label>
            <Input
              id="rp-new"
              data-testid="reset-password-new-input"
              type="password"
              autoComplete="new-password"
              autoFocus
              value={pwd}
              onChange={(e) => setPwd(e.target.value)}
              className="mt-1.5 bg-white"
            />
            <p className="mt-1.5 text-xs text-zinc-500">At least 8 characters.</p>
          </div>
          <div>
            <Label htmlFor="rp-confirm">Confirm new password</Label>
            <Input
              id="rp-confirm"
              data-testid="reset-password-confirm-input"
              type="password"
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="mt-1.5 bg-white"
            />
          </div>

          <Button
            type="submit"
            disabled={submitting}
            data-testid="reset-password-submit"
            className="w-full bg-zinc-950 hover:bg-zinc-800 text-white"
          >
            {submitting ? "Updating…" : "Update password"}
          </Button>
        </form>

        <div className="mt-6 text-center">
          <Link
            to="/login"
            className="inline-flex items-center gap-1.5 text-sm text-zinc-600 hover:text-zinc-950"
          >
            <ArrowLeft className="h-4 w-4" /> Back to sign in
          </Link>
        </div>
      </Card>
    </div>
  );
}
